import { define } from 'custom-elements';
import { money } from 'product-shared';

/**
 * Variant selection for the product form.
 *
 * Reads the product's variant JSON rendered into the page, matches the chosen
 * option values against it, and keeps the hidden `id` field, the price, the
 * gallery, the add button and the URL in step.
 *
 * Availability is handled per option value rather than per variant, so a value
 * that cannot combine with the current selection is dimmed before it is chosen
 * rather than after.
 */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-variant-data]');
    if (!data) return;

    try {
      this.variants = JSON.parse(data.textContent);
    } catch {
      return;
    }

    this.idField = this.querySelector('[data-variant-id]');
    this.message = this.querySelector('[data-unavailable-message]');

    this.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"]')) this.select();
    });

    this.markUnavailable();
  }

  /** The option values currently chosen, in option order. */
  get selection() {
    return [...this.querySelectorAll('input[type="radio"]:checked')].map((input) => input.value);
  }

  get current() {
    const chosen = this.selection;
    return this.variants.find((variant) =>
      variant.options.every((option, index) => option === chosen[index])
    );
  }

  select() {
    const variant = this.current;

    this.toggleMessage(!variant);
    this.markUnavailable();
    this.updateStepValues();

    if (!variant) {
      this.setButton({ disabled: true, label: window.themeStrings?.unavailable });
      return;
    }

    if (this.idField) this.idField.value = variant.id;

    this.updateUrl(variant);
    this.updatePrice(variant);
    this.updateMedia(variant);
    this.setButton({
      disabled: !variant.available,
      label: variant.available ? null : window.themeStrings?.soldOut,
    });
  }

  /**
   * Dim option values that cannot be reached from the current selection.
   * Each option is tested independently: hold the other choices fixed and ask
   * whether any available variant exists for this value.
   */
  markUnavailable() {
    const chosen = this.selection;

    this.querySelectorAll('.variant-step').forEach((step, index) => {
      step.querySelectorAll('input[type="radio"]').forEach((input) => {
        const candidate = [...chosen];
        candidate[index] = input.value;

        const reachable = this.variants.some(
          (variant) =>
            variant.available &&
            variant.options.every((option, i) => i === index ? option === input.value : option === candidate[i])
        );

        input.toggleAttribute('data-unavailable', !reachable);
      });
    });
  }

  /**
   * A swatch step names its chosen value below the row of circles, since the
   * circles themselves carry no visible text. Read from each step's own checked
   * radio rather than from the matched variant, so the name still follows the
   * click when the resulting combination does not exist.
   */
  updateStepValues() {
    this.querySelectorAll('.variant-step').forEach((step) => {
      const node = step.querySelector('[data-step-value]');
      if (node) node.textContent = step.querySelector('input[type="radio"]:checked')?.value ?? '';
    });
  }

  toggleMessage(show) {
    if (this.message) this.message.hidden = !show;
  }

  /** Keep the address shareable and the back button meaningful. */
  updateUrl(variant) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  /**
   * Every price on the page is updated, not just the first. The price appears
   * both in the standalone price block and inside the add button, and a
   * merchant can order those blocks freely, so neither can be assumed unique.
   */
  updatePrice(variant) {
    const show = variant.compare_at_price > variant.price;

    document.querySelectorAll('[data-product-price]').forEach((node) => {
      node.textContent = money(variant.price);
    });

    document.querySelectorAll('[data-compare-price]').forEach((node) => {
      node.hidden = !show;
      if (show) node.textContent = money(variant.compare_at_price);
    });
  }

  updateMedia(variant) {
    if (!variant.featured_media) return;
    document.querySelector('product-media')?.show(variant.featured_media.position - 1);
  }

  /**
   * The button carries a stack of labels — one per state — that product-form.js
   * switches between while adding. A sold-out or unreachable variant is one more
   * state, so its wording goes in the `alt` slot rather than overwriting the
   * stack, which would leave the button with nothing to say afterwards.
   */
  setButton({ disabled, label }) {
    const button = document.querySelector('[data-add-button]');
    if (!button) return;

    button.disabled = disabled;
    button.dataset.state = label ? 'alt' : 'idle';

    const alt = button.querySelector('[data-add-label-alt]');
    if (alt && label) alt.textContent = label;

    // A button rendered without the stack (an older or simpler form) still gets
    // its single label written, as before.
    const text = button.querySelector('[data-add-label]');
    if (text && !alt) text.textContent = label || button.dataset.defaultLabel || text.textContent;
  }
}

define('variant-picker', VariantPicker);

/**
 * The product gallery.
 *
 * Defined here rather than alongside its markup in snippets/product-media.liquid
 * so that it shares this file's single module with VariantPicker, which calls
 * `show()` on it from updateMedia(). Split across two scripts the caller was
 * deferred and the callee async, so a variant change during the async load
 * window could reach an element that had not upgraded yet.
 */
class ProductMedia extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-media-track]');

    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-media-thumb]');
      if (thumb) this.show(thumb.dataset.index);

      const arrow = event.target.closest('[data-media-step]');
      if (arrow) this.step(Number(arrow.dataset.mediaStep));
    });

    // Browsing the photos means the customer wants the gallery back.
    this.track?.addEventListener('pointerdown', () => this.feature(null), { passive: true });

    // Open on the active photo, e.g. a ?variant= link to a colour whose photo
    // sits further along. No animation: this is the starting position.
    const active = this.querySelector('[data-media-thumb].is-active');
    if (active) this.show(active.dataset.index, 'auto');

    const strip = this.querySelector('.product-media__thumbs');
    const activeItem = active?.parentElement;
    if (strip && activeItem) {
      if (isColumn(strip)) strip.scrollTop = activeItem.offsetTop - strip.offsetTop;
      else strip.scrollLeft = activeItem.offsetLeft - strip.offsetLeft;
    }

    // A swipe scrolls the track itself, so the thumbnails follow it rather than
    // the other way round. `scrollend` fires once the snap has settled; where it
    // isn't supported, a short idle after the last scroll event stands in.
    const onSettled = () => this.syncThumbs(this.current);
    if ('onscrollend' in window) {
      this.track?.addEventListener('scrollend', onSettled);
    } else {
      let timer = null;
      this.track?.addEventListener(
        'scroll',
        () => {
          clearTimeout(timer);
          timer = setTimeout(onSettled, 120);
        },
        { passive: true }
      );
    }
  }

  get count() {
    return this.querySelectorAll('[data-media-slide]').length;
  }

  /** Whichever photo the track has scrolled to. */
  get current() {
    if (!this.track || !this.track.clientWidth) return 0;
    const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
    return Math.min(Math.max(index, 0), this.count - 1);
  }

  step(delta) {
    if (this.count < 2) return;
    this.show((this.current + delta + this.count) % this.count);
  }

  /**
   * Called by the variant picker when a variant has its own image, and by the
   * arrows and thumbnails.
   *
   * Stepping to the next photo scrolls; jumping further doesn't. A smooth
   * scroll across a dozen slides flickers through every photo on the way,
   * most of them still loading, which is what switching colour does.
   */
  show(index, behavior) {
    this.feature(null);
    const slide = this.querySelector(`[data-media-slide][data-index="${index}"]`);
    if (this.track && slide) {
      const jump = Math.abs(Number(index) - this.current) > 1;
      this.track.scrollTo({
        left: slide.offsetLeft - this.track.offsetLeft,
        behavior: behavior || (jump ? 'auto' : 'smooth'),
      });
    }
    this.syncThumbs(index);
  }

  /**
   * Moves to the slide showing this photo, matched on its file path so the
   * size and version in the query string don't matter. Returns false when the
   * gallery has no such photo.
   */
  showSource(src) {
    const path = (url) => new URL(url, location.href).pathname;
    const wanted = path(src);
    const slide = [...this.querySelectorAll('[data-media-slide]')].find((candidate) => {
      const image = candidate.querySelector('img');
      return image && path(image.getAttribute('src')) === wanted;
    });
    if (!slide) return false;
    this.show(slide.dataset.index);
    return true;
  }

  /**
   * Lays a photo that is not part of the product's media over the gallery,
   * such as the kit builder's shot of the chosen case with the chosen ball.
   * It sits on top of the track rather than joining it, so slide positions
   * the variant picker relies on never shift. `null` takes it away, as does
   * any move through the gallery's own photos.
   */
  feature(src, srcset) {
    let image = this.querySelector('[data-media-feature]');

    if (!src) {
      image?.remove();
      return;
    }

    if (!image) {
      image = document.createElement('img');
      image.className = 'product-media__feature';
      image.dataset.mediaFeature = '';
      image.alt = '';
      image.sizes = '(min-width: 990px) 45vw, 100vw';
      this.track?.after(image);
    }

    if (srcset) image.srcset = srcset;
    image.src = src;
  }

  syncThumbs(index) {
    this.querySelectorAll('[data-media-thumb]').forEach((thumb) => {
      const active = thumb.dataset.index === String(index);
      thumb.classList.toggle('is-active', active);
      if (active) {
        thumb.setAttribute('aria-current', 'true');
        // Keep the active thumbnail in view, without scrolling the page. The
        // strip is a row under the photo, or a column beside it on desktop.
        const strip = thumb.closest('.product-media__thumbs');
        const item = thumb.parentElement;
        if (strip && isColumn(strip)) {
          const top = item.offsetTop - strip.offsetTop;
          if (top < strip.scrollTop || top + item.offsetHeight > strip.scrollTop + strip.clientHeight) {
            strip.scrollTo({ top, behavior: 'smooth' });
          }
        } else if (strip) {
          const left = item.offsetLeft - strip.offsetLeft;
          if (left < strip.scrollLeft || left + item.offsetWidth > strip.scrollLeft + strip.clientWidth) {
            strip.scrollTo({ left, behavior: 'smooth' });
          }
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

define('product-media', ProductMedia);

function isColumn(strip) {
  return getComputedStyle(strip).flexDirection === 'column';
}

/**
 * Colour dots over the main photo on a phone, so a customer can change the case
 * colour where they are looking instead of scrolling down to the picker and
 * back. They are copies of the picker's first picture or colour step, and each
 * one presses the matching option there, so the price, photo and starter kit
 * follow exactly as they would for a tap on the picker itself.
 */
class MediaSwatches extends HTMLElement {
  connectedCallback() {
    const form = this.closest('.product')?.querySelector('.product__form');
    const step = [...(form?.querySelectorAll('.variant-step') || [])].find((candidate) =>
      candidate.querySelector('.variant-option__media')
    );
    const inputs = step ? [...step.querySelectorAll('input[type="radio"]')] : [];
    if (inputs.length < 2) return;

    this.replaceChildren(
      ...inputs.map((input) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-media__swatch';
        button.dataset.value = input.value;
        button.setAttribute('aria-label', input.value);
        const media = input.labels?.[0]?.querySelector('.variant-option__media');
        if (media) button.append(media.cloneNode(true));
        button.addEventListener('click', () => {
          if (!input.checked) input.click();
          this.mark(input.value);
        });
        return button;
      })
    );
    this.hidden = false;
    this.mark(inputs.find((input) => input.checked)?.value);

    // The colour also changes from the picker itself and from the starter
    // kit's own colour step, so the dots follow both.
    document.addEventListener('change', (event) => {
      const { name, value } = event.target;
      if (name === inputs[0].name || name === 'kit-option-0') this.mark(value);
    });
  }

  mark(value) {
    const flat = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    this.querySelectorAll('.product-media__swatch').forEach((button) => {
      button.setAttribute('aria-pressed', String(flat(button.dataset.value) === flat(value)));
    });
  }
}

define('media-swatches', MediaSwatches);
